/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import { updatePathWithTabs } from "../../utils/Routes";
import { getUser } from "../../utils/helpers";
import OverdueTab from "../Components/DataOpsAnalytics/tabs/OverdueTab";
import RetestTab from "../Components/DataOpsAnalytics/tabs/RetestTab";
import DataOpsRcaHubTab from "../Components/DataOpsAnalytics/tabs/DataOpsRcaHubTab";

const AntTabs = withStyles({
  root: {
    backgroundColor: "#F4F5F7",
    borderBottom: "1px solid #e8e8e8",
  },
  indicator: {
    backgroundColor: "#494B83",
  },
})(Tabs);

const AntTab = withStyles((theme) => ({
  root: {
    textTransform: "none",
    minWidth: 160,
    fontWeight: theme.typography.fontWeightRegular,
    marginRight: theme.spacing(4),
    fontSize: "14px",
    lineHeight: "24px",
    color: "#6B778C",
    fontFamily: ["Roboto Bold"].join(","),
    "&:hover": {
      color: "#494B83",
      opacity: 1,
    },
    "&$selected": {
      color: "#494B83",
      fontWeight: theme.typography.fontWeightMedium,
    },
    "&:focus": {
      color: "#494B83",
    },
  },
  selected: {},
}))((props) => <Tab disableRipple {...props} />);

const useStyles = makeStyles({
  root: {
    flexGrow: 1,
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
  },
  panel: {
    backgroundColor: "#F4F5F7",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
  },
  tabPanel: {
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
  },
});

const DATA_OPS_ANALYTICS_TABS = [
  {
    tabLabel: "Overdue",
    route: "overdue",
    permission: "view_data_ops_dashboard_overdue",
    component: <OverdueTab />,
  },
  {
    tabLabel: "Retest",
    route: "retest",
    permission: "view_data_ops_dashboard_retest_info",
    component: <RetestTab />,
  },
  {
    tabLabel: "RCA",
    route: "rca",
    permission: "view_data_ops_dashboard_rca_info",
    component: <DataOpsRcaHubTab />,
  },
];

function TabPanel({
  children, value, index, ...other
}) {
  const classes = useStyles();

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`data-ops-analytics-tabpanel-${index}`}
      aria-labelledby={`data-ops-analytics-tab-${index}`}
      className={classes.tabPanel}
      {...other}
    >
      {value === index && (
        <div className={classes.tabPanel}>
          {children}
        </div>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

TabPanel.defaultProps = {
  children: null,
};

function getTabAccessibilityProps(index) {
  return {
    id: `data-ops-analytics-tab-${index}`,
    "aria-controls": `data-ops-analytics-tabpanel-${index}`,
  };
}

function buildAuthorizedDataOpsTabs(permissionMap = {}) {
  return DATA_OPS_ANALYTICS_TABS
    .filter((item) => Boolean(permissionMap[item.permission]))
    .map((item, index) => ({ ...item, tabValue: index }));
}

function getInitialTabValue(authorizedTabs) {
  const path = window.location.pathname;
  const matchingTab = authorizedTabs.find(
    (item) => path === `/data-ops-analytics/${item.route}`,
  );
  return matchingTab ? matchingTab.tabValue : 0;
}

export default function DataOpsAnalyticsTab() {
  const classes = useStyles();
  const history = useHistory();
  const pageName = "data-ops-analytics";
  const user = getUser();
  const authorizedTabs = buildAuthorizedDataOpsTabs(user?.permissionMap);
  const [activeTabValue, setActiveTabValue] = useState(
    () => getInitialTabValue(authorizedTabs),
  );

  useEffect(() => {
    const path = window.location.pathname;
    updatePathWithTabs(path, undefined, pageName, setActiveTabValue, authorizedTabs, history);

    const unlisten = history.listen((location, action) => {
      if (action === "POP") {
        updatePathWithTabs(
          location.pathname,
          undefined,
          pageName,
          setActiveTabValue,
          authorizedTabs,
          history,
        );
      }
    });

    return () => {
      unlisten();
    };
  }, [history]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (event, nextTabValue) => {
    updatePathWithTabs(
      undefined,
      nextTabValue,
      pageName,
      setActiveTabValue,
      authorizedTabs,
      history,
    );
    setActiveTabValue(nextTabValue);
  };

  if (authorizedTabs.length === 0) {
    return null;
  }

  return (
    <div className={classes.root}>
      <div className={classes.panel}>
        <AntTabs
          value={activeTabValue}
          onChange={handleTabChange}
          aria-label="Data Ops Analytics tabs"
        >
          {authorizedTabs.map((item) => (
            <AntTab
              key={item.route}
              label={item.tabLabel}
              {...getTabAccessibilityProps(item.tabValue)}
            />
          ))}
        </AntTabs>
        {authorizedTabs.map((item) => (
          <TabPanel key={item.route} value={activeTabValue} index={item.tabValue}>
            {item.component}
          </TabPanel>
        ))}
      </div>
    </div>
  );
}
